import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_LINES = [
  "Practitioner-level certifications, scored on shipped artifacts — not quiz banks.",
  "AI Jobs marketplace with role-aware matching and AI-powered resume building.",
  "Pitch Hub for practitioner-founders to be discovered, funded, and scaled.",
  "Community, mentor office hours, and continuously-updated curriculum.",
];

export default function PlatformDebut() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { email: email.trim(), source: "platform_debut" });
      setDone(true);
      toast.success("You're on the list. We'll reach out when access opens.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden section-dark"
      data-testid="platform-debut"
    >
      {/* Ambient orbs + grid */}
      <span
        className="orb"
        style={{ width: 460, height: 460, top: -160, left: "-8%", background: "#4F46E5", opacity: 0.4 }}
      />
      <span
        className="orb"
        style={{ width: 380, height: 380, bottom: -150, right: "-6%", background: "#C9A961", opacity: 0.28 }}
      />
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(250,250,247,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,247,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="container-edge relative py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: announcement */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border"
                style={{
                  borderColor: "rgba(201,169,97,0.45)",
                  background: "rgba(201,169,97,0.10)",
                }}
              >
                <Sparkles size={14} strokeWidth={1.75} style={{ color: "var(--accent-gold)" }} />
                <span
                  className="font-mono text-[11px]"
                  style={{ color: "var(--accent-gold)", letterSpacing: "0.18em" }}
                >
                  DEBUTING · Q3 2026
                </span>
              </div>
            </Reveal>

            <Reveal>
              <h2
                className="mt-6 font-display tracking-tight leading-[0.96]"
                style={{
                  fontWeight: 400,
                  fontSize: "clamp(42px, 6vw, 84px)",
                  color: "var(--canvas)",
                }}
              >
                The main platform
                <br />
                <em className="italic" style={{ fontWeight: 400, color: "var(--accent-gold)" }}>
                  is coming.
                </em>
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <p
                className="mt-6 max-w-[640px] text-[17px] md:text-[19px] leading-relaxed"
                style={{ color: "rgba(250,250,247,0.78)" }}
              >
                One product surface for the full AI journey. Certifications, the Innovation Arena,
                AI Jobs, the Pitch Hub, and the community — unified. We're building it now, with
                the first 1,000 practitioners shaping it. Reserve early access — invites roll out
                in waves, by audience.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <ul className="mt-8 space-y-3">
                {FEATURE_LINES.map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[14.5px] leading-relaxed"
                    style={{ color: "rgba(250,250,247,0.85)" }}
                  >
                    <span
                      className="mt-[10px] inline-block w-4 h-px shrink-0"
                      style={{ background: "var(--accent-gold)" }}
                    />
                    {line}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Right: early-access capture */}
          <div className="lg:col-span-5">
            <Reveal delay={140}>
              <div
                className="p-7 lg:p-8 rounded-sm border"
                style={{
                  background: "rgba(250,250,247,0.04)",
                  borderColor: "rgba(250,250,247,0.16)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="early-access-card"
              >
                <p className="mono-label-gold mb-3">EARLY ACCESS · WAITLIST</p>
                <h3
                  className="font-display text-[26px] md:text-[30px] leading-tight"
                  style={{ fontWeight: 500, color: "var(--canvas)" }}
                >
                  Be first in line when the platform opens.
                </h3>
                <p
                  className="mt-3 text-[14px] leading-relaxed"
                  style={{ color: "rgba(250,250,247,0.7)" }}
                >
                  Drop your email. We'll send your invite when your audience wave opens — and
                  nothing else.
                </p>

                {done ? (
                  <div
                    className="mt-6 p-5 rounded-sm border"
                    style={{
                      borderColor: "rgba(201,169,97,0.45)",
                      background: "rgba(201,169,97,0.08)",
                    }}
                    data-testid="early-access-success"
                  >
                    <p className="mono-label-gold">YOU'RE ON THE LIST</p>
                    <p className="mt-2 text-[14.5px]" style={{ color: "rgba(250,250,247,0.85)" }}>
                      Thanks. We'll reach out when access opens for your audience wave.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="mt-6" data-testid="early-access-form">
                    <label
                      htmlFor="ea-email"
                      className="block font-mono text-[11px] mb-2"
                      style={{ color: "rgba(250,250,247,0.55)", letterSpacing: "0.14em" }}
                    >
                      EMAIL
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        id="ea-email"
                        type="email"
                        required
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 px-4 py-3 text-[15px] rounded-sm focus:outline-none transition-colors"
                        style={{
                          background: "rgba(10,14,31,0.4)",
                          border: "1px solid rgba(250,250,247,0.22)",
                          color: "var(--canvas)",
                        }}
                        data-testid="early-access-email"
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-gold disabled:opacity-60"
                        data-testid="early-access-submit"
                      >
                        {loading ? "Reserving…" : (
                          <>
                            Reserve <ArrowRight size={16} strokeWidth={1.75} />
                          </>
                        )}
                      </button>
                    </div>
                    <p
                      className="mt-3 font-mono text-[11px]"
                      style={{ color: "rgba(250,250,247,0.45)", letterSpacing: "0.06em" }}
                    >
                      No spam. One email when your wave opens.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
